import React from 'react';
import { Shot as BaseShot, Num, Note, Toc } from './parts';

// Инструкция по созданию событий — чешский текст
const lang = 'cs';
const Shot = (props) => <BaseShot lang={lang} {...props} />;

const GuideContent = () => {
  return (
    <div className="guide-content">
      <p className="guide-lead">
        Událost se vytváří jedním průchodem formulářem „Vytvořit novou událost“. Nejprve zvolte typ události
        a způsob, jakým se do ní dostanou závody — podle toho formulář zobrazí příslušná pole. Každý krok
        a každá možnost jsou níže popsány se snímky obrazovky.
      </p>

      <Toc
        title="Obsah"
        items={[
          ['access', 'Než začnete'],
          ['choose', 'Výběr druhu události'],
          ['basics', 'Krok 1. Základ: název, popis, spoluorganizátoři'],
          ['type', 'Krok 2. Typ události'],
          ['status', 'Krok 3. Stav a obrázek'],
          ['registration', 'Krok 4. Přihlášky a viditelnost'],
          ['scoring', 'Krok 5. Soutěž a body'],
          ['wildcards', 'Krok 6. Divoké karty (závod a turnaj)'],
          ['sync', 'Možnost A. Závody nalezené podle šablony vyhledávání'],
          ['manual', 'Možnost B. Závody vytvořené jinými uživateli ROUVY'],
          ['open', 'Možnost C. Otevřený turnaj'],
          ['official', 'Možnost D. Oficiální událost'],
          ['after', 'Po vytvoření: úpravy a spoluorganizátoři'],
          ['problems', 'Časté chyby']
        ]}
      />

      <section id="access">
        <h2>Než začnete</h2>
        <ul>
          <li>
            Tlačítko <strong>„Vytvořit událost“</strong> <Num>1</Num> na stránce Události se zobrazuje jen účtům,
            které smějí události vytvářet. Pokud ho nevidíte, obraťte se na administrátora.
          </li>
          <li>Tlačítko <strong>„Typy událostí“</strong> <Num>2</Num> otevře stručného průvodce typy.</li>
          <li>
            Typ <strong>„Otevřený turnaj“</strong> je dostupný jen účtům se zvláštním přístupem. Ostatní účty
            ho v seznamu typů nevidí.
          </li>
          <li>Aby se závody našly podle šablony vyhledávání, musí mít autor události nebo spoluorganizátor připojené ROUVY.</li>
        </ul>
        <Shot
          name="events-list"
          alt="Stránka Události: tlačítka „Typy událostí“ a „Vytvořit událost“"
          caption="Stránka Události. Ikona tužky na kartě události (3) otevře úpravy — vidí ji autor události a administrátoři."
        />
      </section>

      <section id="choose">
        <h2>Výběr druhu události</h2>
        <p>Druh události určují dvě rozhodnutí: <strong>typ</strong> a <strong>způsob přidávání závodů</strong>.</p>

        <h3>Typy událostí</h3>
        <div className="guide-table-wrap">
          <table className="guide-table">
            <thead>
              <tr>
                <th>Typ</th>
                <th>Trasy</th>
                <th>Harmonogram</th>
                <th>Limit závodů na etapu</th>
                <th>Divoké karty</th>
                <th>K čemu se hodí</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>Skupinová jízda</strong></td>
                <td>1</td>
                <td>Ano</td>
                <td>—</td>
                <td>—</td>
                <td>Společná vyjížďka nebo trénink s přáteli</td>
              </tr>
              <tr>
                <td><strong>Závod</strong></td>
                <td>1</td>
                <td>Ano</td>
                <td>Ano</td>
                <td>Ano</td>
                <td>Jeden závod, případně s několika starty</td>
              </tr>
              <tr>
                <td><strong>Turnaj</strong></td>
                <td>1 a více</td>
                <td>Ano</td>
                <td>Ano</td>
                <td>Ano</td>
                <td>Série etap na různých trasách</td>
              </tr>
              <tr>
                <td><strong>Otevřený turnaj</strong></td>
                <td>1 a více</td>
                <td>Ne, jen období</td>
                <td>—</td>
                <td>—</td>
                <td>Jezdci jezdí trasy, kdy chtějí; počítá se nejlepší čas</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="guide-small">„Trasy“ udávají, kolik různých tras ROUVY může událost mít. Jedna trasa může mít několik startů.</p>

        <h3>Způsoby přidávání závodů</h3>
        <div className="guide-table-wrap">
          <table className="guide-table">
            <thead>
              <tr>
                <th>Způsob</th>
                <th>Kdo vytváří závody v ROUVY</th>
                <th>Typy událostí</th>
                <th>Jak zapnout</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><a href="#sync"><strong>A. Šablona vyhledávání</strong></a></td>
                <td>Autor události nebo spoluorganizátoři</td>
                <td>Skupinová jízda, závod, turnaj</td>
                <td>Výchozí</td>
              </tr>
              <tr>
                <td><a href="#manual"><strong>B. Ručně</strong></a></td>
                <td>Kterýkoli uživatel ROUVY</td>
                <td>Skupinová jízda, závod, turnaj</td>
                <td>Zaškrtávací pole „Závody vytvořené jinými uživateli ROUVY“</td>
              </tr>
              <tr>
                <td><a href="#open"><strong>C. Trasy bez harmonogramu</strong></a></td>
                <td>Každý jezdec sám pro sebe</td>
                <td>Otevřený turnaj</td>
                <td>Zvolte typ „Otevřený turnaj“</td>
              </tr>
              <tr>
                <td><a href="#official"><strong>D. Oficiální událost</strong></a></td>
                <td>ROUVY</td>
                <td>Všechny kromě otevřeného turnaje</td>
                <td>Stav „Oficiální“</td>
              </tr>
            </tbody>
          </table>
        </div>
        <Note kind="warning">
          Způsob přidávání závodů se volí při vytvoření a později ho nelze změnit. Pokud jste zvolili špatně,
          vytvořte událost znovu.
        </Note>
      </section>

      <section id="basics">
        <h2>Krok 1. Základ: název, popis, spoluorganizátoři</h2>
        <Shot name="create-basics" alt="Horní část formuláře pro vytvoření události" caption="Horní část formuláře pro možnost se šablonou vyhledávání" />
        <ol>
          <li><Num>1</Num> <strong>Název události</strong> — povinné. Bez šablony vyhledávání se závody ROUVY hledají podle tohoto názvu.</li>
          <li>
            <Num>2</Num> <strong>Závody vytvořené jinými uživateli ROUVY</strong> — zapněte jen pro <a href="#manual">možnost B</a>.
            Pole šablony pak zmizí a objeví se seznam závodů.
          </li>
          <li>
            <Num>3</Num> <strong>Šablona vyhledávání Rouvy</strong> — část názvu společná všem vašim závodům ROUVY této události,
            např. <code>Autumn Cup</code>. Viz <a href="#sync">možnost A</a>.
          </li>
          <li><Num>4</Num> <strong>Spoluorganizátoři</strong> — uživatelé, kteří mohou také přidávat závody. Jejich závody ROUVY se podle šablony najdou také.</li>
          <li><Num>5</Num> <strong>Popis</strong> — pravidla, formát, odkazy. Jezdci ho vidí na stránce události.</li>
        </ol>

        <h3>Přidání spoluorganizátora</h3>
        <p>Začněte psát jméno nebo přezdívku (alespoň 2 znaky) a vyberte osobu ze seznamu. Vybraní uživatelé se zobrazí jako štítky; křížek uživatele odebere.</p>
        <Shot name="co-organizers" alt="Hledání spoluorganizátora" caption="Jeden spoluorganizátor je přidán, druhý se vybírá ze seznamu" />
      </section>

      <section id="type">
        <h2>Krok 2. Typ události</h2>
        <p>Pod seznamem typů formulář zobrazí nápovědu ke zvolenému typu. Typ určuje, která pole následují.</p>
        <div className="guide-shot-grid">
          <Shot name="type-group_ride" alt="Typ Skupinová jízda" caption="Skupinová jízda: jedna trasa, bez limitu závodů a divokých karet" />
          <Shot name="type-race" alt="Typ Závod" caption="Závod: jedna trasa, přibývá limit závodů na etapu a divoké karty" />
          <Shot name="type-tournament" alt="Typ Turnaj" caption="Turnaj: několik tras, stejná dodatečná pole jako u závodu" />
          <Shot name="type-open_tournament" alt="Typ Otevřený turnaj" caption="Otevřený turnaj: přibývá období turnaje" />
        </div>
      </section>

      <section id="status">
        <h2>Krok 3. Stav a obrázek</h2>
        <Shot name="status-image" alt="Stav a obrázek události" />
        <ol>
          <li>
            <Num>1</Num> <strong>Stav</strong>: „Vlastní“ se hodí téměř pro každou událost. „Oficiální“ je pro oficiální události ROUVY,
            viz <a href="#official">možnost D</a>.
          </li>
          <li>
            <Num>2</Num> <strong>Obrázek</strong> — obálka karty události. Použijte „Nahrát obrázek“, max. 5 MB.
            „Odebrat obrázek“ vybraný obrázek zruší.
          </li>
        </ol>
      </section>

      <section id="registration">
        <h2>Krok 4. Přihlášky a viditelnost</h2>
        <Shot name="registration" alt="Nastavení přihlášek" caption="Turnaj: „Závodů na etapu“ existuje jen u závodů a turnajů" />
        <ol>
          <li>
            <Num>1</Num> <strong>Uzávěrka přihlášek (hodin před startem)</strong> — kolik hodin před prvním závodem
            se přihlášky uzavřou. Prázdné — jezdci se mohou přihlásit i po startu.
          </li>
          <li>
            <Num>2</Num> <strong>Závodů na etapu</strong> (závod a turnaj) — kolik závodů jedné etapy může jezdec odjet do pořadí.
            Jezdci se přihlašují do konkrétních závodů a počítají se jen ty. Prázdné — počítají se všechny závody.
          </li>
          <li><Num>3</Num> <strong>Dlouhá událost</strong> — přihlášky zůstávají otevřené po celou dobu, i po všech závodech.</li>
          <li>
            <Num>4</Num> <strong>Skrytá událost</strong> — v seznamu událostí ji vidí jen administrátoři a organizátoři.
            Výsledky se počítají jako obvykle a přímý odkaz funguje pro všechny.
          </li>
        </ol>
      </section>

      <section id="scoring">
        <h2>Krok 5. Soutěž a body</h2>
        <Shot name="scoring" alt="Soutěž a body" caption="Týmová soutěž je zapnutá — objevila se týmová pole" />
        <ol>
          <li><Num>1</Num> <strong>Soutěž jednotlivců</strong> a/nebo <strong>Týmová soutěž</strong>. Zapnuté mohou být obě.</li>
          <li><Num>2</Num> <strong>Body za 1. místo</strong> — kolik dostane vítěz etapy (výchozí 1000).</li>
          <li><Num>3</Num> <strong>Krok bodů</strong> — o kolik méně dostane každé další místo (výchozí 10).</li>
          <li><Num>4</Num> <strong>Započítaní členové týmu</strong> (jen týmová soutěž) — kolik nejlepších výsledků týmu se započítá.</li>
          <li>
            <Num>5</Num> <strong>Max. počet členů týmu</strong> (jen týmová soutěž) — kolik jezdců jednoho týmu se může přihlásit.
            Prázdné — bez omezení.
          </li>
          <li><Num>6</Num> <strong>Vytvořit</strong> — uloží událost a vrátí se na seznam událostí.</li>
        </ol>
        <Note>
          Body za umístění: <code>body za 1. místo − (místo − 1) × krok</code>, ale ne méně než 0.
          Při 1000 a 10: 1. místo — 1000, 2. — 990, 10. — 910, 101. a horší — 0.
        </Note>
      </section>

      <section id="wildcards">
        <h2>Krok 6. Divoké karty (závod a turnaj)</h2>
        <p>Divoká karta umožní jezdci odjet etapu mimo harmonogram. Blok existuje jen u vlastních událostí typu Závod a Turnaj.</p>
        <ol>
          <li><strong>Divoké karty (max. počet)</strong> — kolik divokých karet může jezdec v události využít. Prázdné — bez divokých karet.</li>
          <li>
            <strong>Kdo vytváří divokou kartu</strong> (zobrazí se, když je divokých karet víc než 0):
            <ul>
              <li><strong>Vytváří organizátor</strong> — jezdec uvede datum a čas, organizátor pro něj vytvoří závod v ROUVY a žádost schválí.</li>
              <li><strong>Vytváří účastník</strong> — jezdec vytvoří závod v ROUVY podle vašich požadavků a pošle odkaz; organizátor ho schválí nebo zamítne.</li>
            </ul>
          </li>
          <li>
            <strong>Pole s požadavky</strong> (jen „Vytváří účastník“) — čemu musí závod jezdce odpovídat: trasa, nastavení, čas.
            Jezdci tento text uvidí při podávání žádosti.
          </li>
        </ol>
        <div className="guide-shot-grid">
          <Shot name="wildcards-organizer" alt="Divoké karty vytváří organizátor" caption="Vytváří organizátor" />
          <Shot name="wildcards-participant" alt="Divoké karty vytváří účastník" caption="Vytváří účastník: objeví se požadavky" />
        </div>
        <p>O každé žádosti dostane organizátor oznámení.</p>
      </section>

      <section id="sync">
        <h2>Možnost A. Závody nalezené podle šablony vyhledávání</h2>
        <p>Toto je výchozí možnost. Vy (nebo spoluorganizátoři) vytvoříte závody v ROUVY a systém je podle šablony najde a přidá do události.</p>
        <ol className="guide-steps">
          <li>Vytvořte v ROUVY závody se společnou částí v každém názvu, např. <code>Autumn Cup #1</code>, <code>Autumn Cup #2</code>.</li>
          <li>Zaškrtávací pole „Závody vytvořené jinými uživateli ROUVY“ nechte <strong>vypnuté</strong>.</li>
          <li>Společnou část zadejte do <strong>„Šablona vyhledávání Rouvy“</strong>: <code>Autumn Cup</code>. Prázdné — použije se název události.</li>
          <li>Pokud závody vytvářejí i další lidé, přidejte spoluorganizátory.</li>
          <li>Vyplňte zbývající kroky a klikněte na <strong>„Vytvořit“</strong>.</li>
          <li>Hledání závodů se spustí na pozadí hned po vytvoření. Závody se objeví během několika minut — zkontrolujte je v úpravách události.</li>
        </ol>
        <p>V úpravách události je blok synchronizace a seznam nalezených závodů:</p>
        <Shot name="edit-sync" alt="Synchronizace a přidané závody" />
        <ol>
          <li><Num>1</Num> <strong>Synchronizovat trasy</strong> — hledat znovu, např. po vytvoření nových závodů v ROUVY. Stránku můžete zavřít.</li>
          <li><Num>2</Num> <strong>Obnovit seznam</strong> — načte výsledek synchronizace bez obnovení stránky.</li>
          <li><Num>3</Num> <strong>Smazat</strong> — odebere nechtěný závod z události.</li>
        </ol>
        <Note>Uložení změněného názvu události nebo šablony spustí hledání automaticky.</Note>
      </section>

      <section id="manual">
        <h2>Možnost B. Závody vytvořené jinými uživateli ROUVY</h2>
        <p>Pokud závody v ROUVY nevytvořil nikdo z vás ani ze spoluorganizátorů, hledání podle šablony je nenajde. Závody pak přidejte ručně.</p>
        <ol className="guide-steps">
          <li>Zapněte <strong>„Závody vytvořené jinými uživateli ROUVY“</strong>. Pole šablony zmizí a objeví se blok „Závody události“.</li>
          <li>
            U každého závodu vyplňte:
            <ul>
              <li><strong>Trasa ROUVY*</strong> — ID trasy nebo odkaz ve tvaru <code>https://riders.rouvy.com/route/12345</code>;</li>
              <li><strong>Název trasy</strong> — nepovinné, pokud chcete vlastní;</li>
              <li><strong>Odkaz na závod ROUVY*</strong> — <code>https://riders.rouvy.com/events/…</code>;</li>
              <li><strong>Název závodu</strong> — nepovinné, prázdné — převezme se z ROUVY;</li>
              <li><strong>Datum a čas startu*</strong> — ve vašem časovém pásmu.</li>
            </ul>
          </li>
          <li><strong>„Přidat závod“</strong> přidá řádek, <strong>„Odebrat“</strong> ho smaže. Prázdné řádky se neukládají.</li>
          <li>Klikněte na <strong>„Vytvořit“</strong>. Pokud některý závod neprojde, událost se nevytvoří a chyba uvede číslo řádku.</li>
        </ol>
        <Shot name="manual-create" alt="Závody události při vytváření" caption="Seznam závodů při vytváření události" />
        <p>Závody můžete přidat i později: v úpravách události je formulář pro jeden závod <Num>1</Num>. Přidaný závod se hned zobrazí v seznamu <Num>2</Num>.</p>
        <Shot name="edit-manual" alt="Přidání závodu v úpravách" />
      </section>

      <section id="open">
        <h2>Možnost C. Otevřený turnaj</h2>
        <p>
          Otevřený turnaj nemá harmonogram. Každý jezdec si vytvoří závod v ROUVY (Race) na trase turnaje a jede ho, kdy chce.
          Jízda se počítá podle trasy a typu závodu a do pořadí jde nejlepší čas ze všech pokusů.
        </p>
        <ol className="guide-steps">
          <li>V poli „Typ události“ zvolte <strong>„Otevřený turnaj“</strong>.</li>
          <li>
            Nastavte <strong>období turnaje</strong>: „Začátek turnaje“ a „Konec turnaje“. Jízdy mimo něj se nepočítají.
            Obě hodnoty jsou nepovinné: bez nich turnaj nikdy nekončí. Konec musí být později než začátek.
          </li>
          <li>Vyplňte zbývající kroky a klikněte na <strong>„Vytvořit“</strong>.</li>
          <li>Otevřete úpravy události a přidejte trasy (viz níže).</li>
        </ol>
        <Note kind="warning">Trasy otevřeného turnaje se přidávají až po vytvoření události, v jejích úpravách.</Note>
        <Shot name="edit-open" alt="Trasy otevřeného turnaje" />
        <ol>
          <li><Num>1</Num> Vložte odkaz na stránku trasy ROUVY: <code>https://riders.rouvy.com/route/…</code>.</li>
          <li><Num>2</Num> Klikněte na <strong>„Přidat trasu“</strong>.</li>
          <li><Num>3</Num> Trasa se přidá do turnaje. „Smazat“ ji odebere.</li>
        </ol>
      </section>

      <section id="official">
        <h2>Možnost D. Oficiální událost</h2>
        <p>
          Stav <strong>„Oficiální“</strong> je pro oficiální události ROUVY. Jejich závody spravuje ROUVY, proto je formulář kratší:
          chybí zaškrtávací pole ručních závodů, šablona vyhledávání, spoluorganizátoři i divoké karty.
        </p>
        <Shot name="official" alt="Formulář oficiální události" caption="Stav „Oficiální“ — dodatečné bloky jsou skryté" />
      </section>

      <section id="after">
        <h2>Po vytvoření: úpravy a spoluorganizátoři</h2>
        <ul>
          <li>Úpravy otevřete ikonou tužky na kartě události na stránce Události. Všechna nastavení může měnit autor události a administrátoři.</li>
          <li>Způsob přidávání závodů (zaškrtávací pole „Závody vytvořené jinými uživateli ROUVY“) v úpravách změnit nelze.</li>
          <li>Spoluorganizátoři vidí zjednodušenou stránku: mohou přidávat závody (spustit synchronizaci nebo je zadat ručně) a mazat ty, které přidali. Ostatní nastavení jim nejsou dostupná.</li>
        </ul>
        <Shot name="co-organizer-view" alt="Stránka spoluorganizátora" caption="Co vidí spoluorganizátor: smazat lze jen vlastní závod" />
      </section>

      <section id="problems">
        <h2>Časté chyby</h2>
        <div className="guide-table-wrap">
          <table className="guide-table">
            <thead>
              <tr>
                <th>Hlášení nebo situace</th>
                <th>Příčina a řešení</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>„Název události je povinný“</td>
                <td>Vyplňte „Název události“.</td>
              </tr>
              <tr>
                <td>„Skupinová jízda a závod mohou mít pouze jednu trasu“</td>
                <td>Událost má závody na různých trasách. Zvolte typ Turnaj nebo odeberte přebytečné závody.</td>
              </tr>
              <tr>
                <td>„Turnaj musí skončit později, než začne“</td>
                <td>Opravte období otevřeného turnaje.</td>
              </tr>
              <tr>
                <td>„U každého závodu zadejte trasu, odkaz na závod ROUVY a datum/čas startu“</td>
                <td>V řádku ručního závodu chybí povinné pole. Vyplňte ho nebo řádek odeberte.</td>
              </tr>
              <tr>
                <td>Otevřený turnaj je při uložení odmítnut</td>
                <td>Účet nemá přístup k otevřeným turnajům. Zvolte jiný typ.</td>
              </tr>
              <tr>
                <td>Po synchronizaci žádné závody</td>
                <td>
                  Zkontrolujte, že názvy závodů ROUVY obsahují šablonu, závody vytvořil někdo z vás nebo ze spoluorganizátorů
                  a máte připojené ROUVY. Počkejte několik minut a klikněte na „Obnovit seznam“.
                  Pokud závody vytvořil někdo jiný, potřebujete <a href="#manual">možnost B</a> — vytvořte událost znovu.
                </td>
              </tr>
              <tr>
                <td>Chybí tlačítko „Vytvořit událost“</td>
                <td>Účet nesmí vytvářet události — obraťte se na administrátora.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default GuideContent;
